import { Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { CubeMemberMatchPreviewResponse } from 'types/api/SystemTenant/AROCube/CubeBuilderTypes';

export function MemberMatchPreview({ preview }: { preview: CubeMemberMatchPreviewResponse }) {
  return (
    <Stack spacing={1.5} sx={{ minWidth: 0, borderTop: '1px solid #E3EBF6', pt: 2 }}>
      <Typography variant='input-label' fontWeight='bold' sx={{ overflowWrap: 'anywhere' }}>
        Member match preview: {preview.tenantName}
      </Typography>
      <Box
        component='dl'
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
          gap: 1.5,
          m: 0,
        }}
      >
        {[
          {
            label: 'Matched source members',
            count: preview.matchedSourceCount,
            total: preview.sourceMemberCount,
          },
          {
            label: 'Unmatched source members',
            count: preview.unmatchedSourceCount,
            total: preview.sourceMemberCount,
          },
          {
            label: 'Unmatched destination members',
            count: preview.unmatchedDestinationCount,
            total: preview.destinationMemberCount,
          },
        ].map(({ label, count, total }) => (
          <Box key={label} sx={{ minWidth: 0 }}>
            <Typography component='dt' variant='input-label-gray' sx={{ fontSize: 12 }}>
              {label}
            </Typography>
            <Typography component='dd' variant='input-label' sx={{ m: 0, fontWeight: 700 }}>
              {count}{' '}
              <Box component='span' sx={{ fontWeight: 400, color: 'text.secondary' }}>
                of {total}
              </Box>
            </Typography>
          </Box>
        ))}
      </Box>
      {!!preview.matches.length && (
        <Box sx={{ maxHeight: 220, overflow: 'auto', border: '1px solid #E3EBF6' }}>
          <Table
            size='small'
            stickyHeader
            aria-label='Sample member matches'
            sx={{
              tableLayout: 'fixed',
              '& .MuiTableCell-root': {
                px: 1.5,
                py: 1,
                fontSize: 12,
                borderColor: '#E3EBF6',
                overflowWrap: 'anywhere',
              },
              '& .MuiTableCell-head': {
                bgcolor: '#EDF2FF',
                color: '#3B4797',
                fontWeight: 700,
              },
              '& tbody tr:nth-of-type(even)': { bgcolor: '#F8F8F8' },
              '& tbody tr:last-child td': { borderBottom: 0 },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Source member</TableCell>
                <TableCell>Destination member</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.matches.slice(0, 100).map((match, i) => (
                <TableRow key={`${match.sourceMemberId}:${match.destinationMemberId}:${i}`}>
                  <TableCell>{match.sourceMember}</TableCell>
                  <TableCell>{match.destinationMember}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {[
        {
          side: 'source',
          count: preview.unmatchedSourceCount,
          members: preview.unmatchedSourceMembers,
        },
        {
          side: 'destination',
          count: preview.unmatchedDestinationCount,
          members: preview.unmatchedDestinationMembers,
        },
      ].map(
        ({ side, count, members }) =>
          !!members?.length && (
            <Box component='details' key={side} sx={{ borderTop: '1px solid #E3EBF6', pt: 1 }}>
              <Typography
                component='summary'
                variant='input-label'
                sx={{ cursor: 'pointer', fontSize: 12, fontWeight: 700 }}
              >
                Unmatched {side} members ({count})
              </Typography>
              <Box
                component='ul'
                aria-label={`Unmatched ${side} members`}
                sx={{ my: 1, pl: 3, maxHeight: 160, overflow: 'auto' }}
              >
                {members.slice(0, 100).map((member) => (
                  <Typography
                    component='li'
                    variant='body2'
                    key={member.value}
                    sx={{ fontSize: 12, overflowWrap: 'anywhere', py: 0.25 }}
                  >
                    {member.label}
                  </Typography>
                ))}
              </Box>
            </Box>
          )
      )}
      <Typography variant='caption' color='text.secondary'>
        Sample limit: 100 members per list
      </Typography>
    </Stack>
  );
}
